<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import { api } from '@/api/client.js';
import ContextHelp from '@/components/ds/ContextHelp.vue';
import Tabs from '@/components/ds/Tabs.vue';
import BarRankRow from '@/components/panels/BarRankRow.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';
import { AUDIENCE_EMPTY } from '@/utils/emptyStateCopy.js';

// Idiomas (subtag primario) y timezones por sesiones distintas —
// proxy geográfico agregado. Top-5 visibles por columna.

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const data = ref({ totalSessions: 0, languages: [], timezones: [] });
const activeTab = ref('languages');
const tabs = [{ value: 'languages', label: 'Languages' }, { value: 'timezones', label: 'Timezones' }];
function languageName(value) {
  try { return new Intl.DisplayNames(['en'], { type: 'language' }).of(value) || value; } catch { return value; }
}

async function fetchFn({ signal }) {
  if (!activeAppId.value) return;
  return api.events.audience({ appId: activeAppId.value, window: timeWindow.value, signal });
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey],
  onSuccess(result) {
    if (!result || result.ok !== true) return;
    data.value = {
      totalSessions: result.totalSessions ?? 0,
      languages: result.languages ?? [],
      timezones: result.timezones ?? [],
    };
    const empty = data.value.languages.length === 0 && data.value.timezones.length === 0;
    ps.setState(empty ? 'empty' : 'data');
  },
  onError(err) {
    ps.setState('error', {
      message: "Couldn't load audience data",
      detail: err?.message ?? '',
    });
  },
});

ps.setRetryHandler(() => tick());

// Exponer ps para los panelRefs del LandingView (error global / retry all).
defineExpose({ ps, refreshStatus });

function withPct(rows) {
  const total = data.value.totalSessions;
  return rows.slice(0, 5).map((row) => ({
    ...row,
    pct: total > 0 ? (row.count / total) * 100 : null,
  }));
}

const languages = computed(() => withPct(data.value.languages));
const timezones = computed(() => withPct(data.value.timezones));
const hasRows = computed(() => languages.value.length > 0 || timezones.value.length > 0);
</script>

<template>
  <PanelCard
    title="Audience"
    help-key="audience"
    :state="ps.state.value"
    :empty-headline="AUDIENCE_EMPTY.headline"
    :empty-body="AUDIENCE_EMPTY.body"
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <div class="audience">
      <p class="audience__summary">
        <strong>{{ data.totalSessions.toLocaleString() }}</strong> sessions in this period
      </p>
      <Tabs
        v-model="activeTab"
        :items="tabs"
        size="sm"
      />
      <p class="audience__none">
        Top 5 · sessions and share of all sessions. Languages and timezones do not identify a visitor’s location.
      </p>
      <div
        v-if="activeTab === 'languages'"
        class="audience__col"
      >
        <h3 class="audience__subtitle">
          Languages
          <ContextHelp help-key="languages" />
        </h3>
        <BarRankRow
          v-for="row in languages"
          :key="`lang-${row.value}`"
          :label="languageName(row.value)"
          :count="row.count"
          :pct="row.pct"
        />
        <p
          v-if="languages.length === 0"
          class="audience__none"
        >
          No language data.
        </p>
      </div>
      <div
        v-else
        class="audience__col"
      >
        <h3 class="audience__subtitle">
          Timezones
          <ContextHelp help-key="timezones" />
        </h3>
        <BarRankRow
          v-for="row in timezones"
          :key="`tz-${row.value}`"
          :label="row.value"
          :count="row.count"
          :pct="row.pct"
        />
        <p
          v-if="timezones.length === 0"
          class="audience__none"
        >
          No timezone data.
        </p>
      </div>
    </div>
    <template
      v-if="hasRows"
      #tableFallback
    >
      <table class="au-table">
        <caption class="visually-hidden">
          Audience — languages and timezones by sessions
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Dimension
            </th>
            <th scope="col">
              Value
            </th>
            <th
              scope="col"
              class="numeric"
            >
              Sessions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in languages"
            :key="`tl-${row.value}`"
          >
            <td>Language</td>
            <td>{{ row.value }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
          <tr
            v-for="row in timezones"
            :key="`tt-${row.value}`"
          >
            <td>Timezone</td>
            <td>{{ row.value }}</td>
            <td class="numeric">
              {{ row.count }}
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.audience {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: var(--space-4);
}

@media (width <=639px) {
  .audience {
    grid-template-columns: 1fr;
  }
}

.audience__col {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.audience__subtitle {
  font-size: var(--text-xs);
  font-weight: var(--fw-medium);
  color: var(--fg-secondary);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wide);
  margin: 0 0 var(--space-1);
}

.audience__none {
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
  margin: 0;
}

.audience__summary {
  margin: 0;
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.audience__summary strong {
  font-size: var(--text-2xl);
  color: var(--fg-primary);
  font-variant-numeric: tabular-nums;
}

.au-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--text-xs);
}

.au-table th,
.au-table td {
  padding: var(--space-1) var(--space-2);
  text-align: left;
  border-bottom: var(--bw-thin) solid var(--border-default);
}

.au-table th {
  color: var(--fg-secondary);
  font-weight: var(--fw-medium);
}

.au-table .numeric {
  text-align: right;
  font-feature-settings: "tnum";
}
</style>
